<script lang="ts">
import TowerCard from './TowerCard.vue';
import { ProgramTower } from '@/types/tower/v2/Tower';
import { Check, ChevronDown, Plus } from 'lucide-vue-next';

export default {
  name: 'TowerDashboard',
  components: {
    TowerCard,
    Check,
    ChevronDown,
    Plus,
  },
};
</script>

<script setup lang="ts">
import { onMounted, ref, watch, computed } from 'vue';
import { GetTowerCardsParams, GetTowersParams, towerService } from '@/services/v2/TowerService';
import {
  TowerCardData,
  TowerDashboardRouteQuery,
  TowerType,
  TowerTypeData,
  BOUND_COVERAGE_TOOLTIP,
  NEW_COVERAGE_TOOLTIP,
  RENEWAL_TOOLTIP,
  SCRATCH_TOOLTIP,
} from '@/views/tower_dashboard/types/tower-dashboard';
import { useRoute, useRouter } from 'vue-router';
import CreateTowerModal from '@/views/tower_dashboard/CreateTowerModal.vue';
import { useStore } from 'vuex';
import { AppState, Commit } from '@/store';
import EditTowerModal from '@/views/tower_dashboard/EditTowerModal.vue';
import { StoreMutationType } from '@/types/StoreMutationType';
import DeleteTowerModal from '@/views/tower_dashboard/DeleteTowerModal.vue';
import ConvertScratchTowerModal from './ConvertScratchTowerModal.vue';
import ConvertNewCoverageAndRenewalTowerModal from './ConvertNewCoverageAndRenewalTowerModal.vue';
import { escapeRegex } from '@/utilities/tower/regex';

enum TowerOwnershipType {
  CreatedByMe = 1,
  AllTowers = 2,
  WorkedOnByMe = 3,
}

const DEFAULT_OWNERSHIP_TYPE = TowerOwnershipType.AllTowers;

const store = useStore<AppState>();
const route = useRoute();

// ---------------
// reactive data
// ---------------
const router = useRouter();
const towerCards = ref<TowerCardData[]>([]);
const loading = ref(true);
const showCreateTowerModal = ref(false);
const showEditTowerModal = ref(false);
const deleteTowerState = ref<{
  show: boolean;
  highlightTowerId?: string;
  towerCard?: TowerCardData;
}>({ show: false, highlightTowerId: undefined, towerCard: undefined });
const convertScratchTowerState = ref<{
  show: boolean;
  highlightTowerId?: string;
  towerId?: string;
}>({ show: false, highlightTowerId: undefined, towerId: undefined });
const convertNewCoverageAndRenewalTowerState = ref<{
  show: boolean;
  highlightTowerId?: string;
  towerId?: string;
  accountId?: number;
}>({ show: false, highlightTowerId: undefined, towerId: undefined, accountId: undefined });
const newTowerType = ref(TowerType.BoundCoverage);
const selectedTowerOwnershipType = ref<TowerOwnershipType>(DEFAULT_OWNERSHIP_TYPE);
const searchFilter = ref('');
const searchTimeout = ref<NodeJS.Timeout>();
const selectedTowerType = ref<TowerTypeData>();
const towerTypes = ref<TowerTypeData[]>();
const towerOwnershipFilter = ref<HTMLElement>();
const towerTypeFilter = ref<HTMLElement>();
const editTowerId = ref<string>();
const disableTowers = ref<false>();

// ---------------
// lifecycles
// ---------------
onMounted(async () => {
  loading.value = true;
  towerTypes.value = (await towerService.getTowerTypes()).filter((t) => t.id !== TowerType.MultiTower);
  setSearchCriteriaFromQuery();
  await fetchTowerCards();
  loading.value = false;
});

// ---------------
// watchers
// ---------------
watch(selectedTowerOwnershipType, async () => {
  loading.value = true;
  updateRouteSearchQuery();
  await fetchTowerCards();
  loading.value = false;
});

watch(selectedTowerType, async () => {
  loading.value = true;
  updateRouteSearchQuery();
  await fetchTowerCards();
  loading.value = false;
});

watch(searchFilter, () => {
  if (loading.value) {
    return;
  }
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value);
  }
  searchTimeout.value = setTimeout(() => {
    fetchTowerCards();
    updateRouteSearchQuery();
  }, 400);
});

// ---------------
// computed
// ---------------
const towerTypeLabel = computed(() => {
  return selectedTowerType.value?.name ?? 'All Types';
});

const towerOwnershipTypeLabel = computed(() => {
  switch (selectedTowerOwnershipType.value) {
    case TowerOwnershipType.CreatedByMe:
      return 'Created By Me';
    case TowerOwnershipType.AllTowers:
      return 'All Towers';
    case TowerOwnershipType.WorkedOnByMe:
      return 'Worked On By Me';
    default:
      return 'Unknown Type';
  }
});

const isFiltered = computed(() => {
  return (
    !!selectedTowerType.value ||
    selectedTowerOwnershipType.value !== TowerOwnershipType.AllTowers ||
    !!searchFilter.value
  );
});

const highlightTowerId = computed(() => {
  switch (true) {
    case deleteTowerState.value.show:
      return deleteTowerState.value.highlightTowerId;
    case convertScratchTowerState.value.show:
      return convertScratchTowerState.value.highlightTowerId;
    case convertNewCoverageAndRenewalTowerState.value.show:
      return convertNewCoverageAndRenewalTowerState.value.highlightTowerId;
    default:
      return undefined;
  }
});

const highlightBorderColor = computed(() => {
  switch (true) {
    case deleteTowerState.value.show:
      return 'red-500';
    case convertScratchTowerState.value.show:
      return 'green-500';
    case convertNewCoverageAndRenewalTowerState.value.show:
      return 'green-500';
    default:
      return undefined;
  }
});

// ---------------
// methods
// ---------------
async function fetchTowerCards() {
  const params: GetTowerCardsParams = {
    userId:
      selectedTowerOwnershipType.value === TowerOwnershipType.CreatedByMe ||
      selectedTowerOwnershipType.value === TowerOwnershipType.WorkedOnByMe
        ? store?.state?.user?.user.id
        : undefined,
    searchQuery: searchFilter.value,
    towerTypeId: selectedTowerType.value?.id,
    contributor: selectedTowerOwnershipType.value === TowerOwnershipType.WorkedOnByMe,
  };

  towerCards.value = (await towerService.getTowerCards(params)).filter((t) => t.towerTypeId !== TowerType.MultiTower);
  loading.value = false;
}

function loadTower(towerId: string) {
  router.push(`tower/${towerId}`);
}

function openEditTowerModal(towerId: string) {
  const tower = towerCards.value?.filter((t) => t.towerId === towerId)[0];
  if (tower) {
    editTowerId.value = tower.towerId;
    showEditTowerModal.value = true;
  }
}

function openNewTowerModal(type: TowerType) {
  newTowerType.value = type;
  showCreateTowerModal.value = true;
}

function handleCreateTower(towerId: string) {
  showCreateTowerModal.value = false;
  router.push(`tower/${towerId}`);
}

function handleEditTower(success: boolean) {
  if (success) {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Tower updated successfully' });
  } else {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Error: Could not update tower', color: 'error' });
  }

  showEditTowerModal.value = false;
  fetchTowerCards();
}

async function handleDuplicateTower(towerId: string) {
  // find count of matching tower versions for this tower (by programId and tower type)
  // add 1 to that count to get the new version number
  const tower: ProgramTower = (await towerService.getTowers({ towerIds: [towerId] }))[0];

  if (!tower) {
    return;
  }

  const params = {
    programId: tower.programId ? Number(tower.programId) : 0,
    towerTypeId: tower.towerTypeId,
    towerName: tower.towerName,
  } as GetTowersParams;

  const towers = await towerService.getTowers(params);

  if (!towers || towers.length === 0) {
    return;
  }

  const baseVersionLabel = tower.versionLabel?.replace(/(?: \(\d+\))?$/, '') ?? '';
  const duplicateLabelRegex = new RegExp(`^${escapeRegex(baseVersionLabel)}(?: \\(\\d+\\))?$`);

  const versionLabelCount = towers.reduce((acc, t) => {
    if (t.versionLabel && t.versionLabel.match(duplicateLabelRegex)) {
      acc++;
    }
    return acc;
  }, 0);

  if (versionLabelCount === 0) {
    return;
  } else {
    tower.versionLabel = `${baseVersionLabel} (${versionLabelCount})`;
  }

  await towerService.createTower(tower);
  await fetchTowerCards();
}

function handleConfirmDeleteTower(towerCard: TowerCardData) {
  deleteTowerState.value = {
    show: true,
    highlightTowerId: towerCard.towerId,
    towerCard,
  };
}

async function handleDeleteTower(towerId?: string) {
  if (!towerId) {
    return;
  }

  await towerService.deleteTower(towerId);
  deleteTowerState.value = {
    show: false,
    highlightTowerId: undefined,
    towerCard: undefined,
  };
  await fetchTowerCards();
}

async function handleTogglePublishTower(towerId?: string, published?: boolean) {
  if (!towerId) {
    console.error('No towerId provided');
    return;
  }

  try {
    await towerService.updateTowerInfo(towerId, { published: published ?? false });
  } catch (e) {
    console.error('Error publishing/unpublishing tower', e);
  }

  await fetchTowerCards();
}

function handleOpenConvertScratchTowerModal(towerId: string): void {
  convertScratchTowerState.value = {
    show: true,
    highlightTowerId: towerId,
    towerId: towerId,
  };
}

function handleOpenConvertNewCoverageAndRenewalTowerModal(towerId: string, accountId: number): void {
  convertNewCoverageAndRenewalTowerState.value = {
    show: true,
    highlightTowerId: towerId,
    towerId: towerId,
    accountId,
  };
}

async function handleConvertScratchTower(towerId?: string, accountId?: number): Promise<void> {
  if (!towerId) {
    console.error('No towerId provided');
    return;
  }

  if (!accountId) {
    console.error('No accountId provided');
    return;
  }

  try {
    await towerService.updateTowerInfo(towerId, { accountId, towerTypeId: TowerType.NewCoverage });
  } catch (e) {
    console.error('Error converting Scratch tower to New Coverage tower', e);
  }

  // cleanup
  convertScratchTowerState.value = {
    show: false,
    highlightTowerId: undefined,
    towerId: undefined,
  };

  await fetchTowerCards();
}

async function handleConvertNewCoverageAndRenewalTower(towerId?: string, programId?: number): Promise<void> {
  if (!towerId) {
    console.error('No towerId provided');
    return;
  }

  if (!programId) {
    console.error('No programId provided');
    return;
  }

  try {
    await towerService.updateTowerInfo(towerId, { programId, towerTypeId: TowerType.BoundCoverage });
  } catch (e) {
    console.error('Error converting New Coverage tower to Renewal tower', e);
  }

  // cleanup
  convertNewCoverageAndRenewalTowerState.value = {
    show: false,
    highlightTowerId: undefined,
    towerId: undefined,
    accountId: undefined,
  };

  await fetchTowerCards();
}

function setTowerOwnershipTypeFilter(accountType: TowerOwnershipType) {
  selectedTowerOwnershipType.value = accountType;
  towerOwnershipFilter.value?.blur();
}

function setTowerTypeFilter(towerType?: TowerTypeData) {
  selectedTowerType.value = towerType;
  towerTypeFilter.value?.blur();
}

function resetFilters() {
  selectedTowerType.value = undefined;
  selectedTowerOwnershipType.value = DEFAULT_OWNERSHIP_TYPE;
  searchFilter.value = '';
}

function updateRouteSearchQuery() {
  const query = searchCriteriaAsQuery();
  if (JSON.stringify(route.query) !== JSON.stringify(query)) {
    router.replace({ query }).catch(() => {
      return '';
    });
  }
}

function setSearchCriteriaFromQuery() {
  const { q, ownership, type } = route.query as TowerDashboardRouteQuery;
  if (q || ownership || type) {
    searchFilter.value = q || '';
    selectedTowerOwnershipType.value = (ownership && parseInt(ownership)) || DEFAULT_OWNERSHIP_TYPE;

    const towerTypeId = type && parseInt(type);
    if (towerTypeId) {
      selectedTowerType.value = towerTypes.value?.find((t) => t.id === towerTypeId);
    }
  }
}

function searchCriteriaAsQuery() {
  const query = {} as TowerDashboardRouteQuery;

  const towerOwnershipType = selectedTowerOwnershipType.value;
  if (towerOwnershipType !== DEFAULT_OWNERSHIP_TYPE) query.ownership = `${towerOwnershipType}`;
  if (searchFilter.value) query.q = searchFilter.value;
  if (selectedTowerType.value) query.type = `${selectedTowerType.value.id}`;

  return query;
}
</script>

<template>
  <div class="tw:flex tw:h-full tw:flex-col tw:pt-6">
    <!-- Overlay to disable editing while card options are open -->
    <div
      v-if="disableTowers"
      @click="$event.stopPropagation()"
      class="tw:pointer-events-all tw:absolute tw:z-10 tw:h-full tw:w-full tw:cursor-auto"></div>

    <!-- options -->
    <div class="tw:z-50 tw:flex tw:w-10/12 tw:flex-row tw:items-center tw:justify-center tw:gap-6 tw:self-center">
      <!-- Search bar -->
      <input
        type="text"
        placeholder="Search"
        v-model="searchFilter"
        class="tw:input tw:w-64 tw:border-solid tw:shadow-xs" />

      <!-- Tower Ownership filter -->
      <div class="tw:dropdown tw:dropdown-bottom">
        <label
          tabindex="0"
          class="tw:btn tw:m-1 tw:rounded-md tw:bg-gray-200/50 tw:text-xs tw:shadow-xs tw:hover:bg-gray-200/100">
          {{ towerOwnershipTypeLabel }}
          <ChevronDown :size="16" class="tw:font-bold tw:text-gray-600" />
        </label>
        <ul
          ref="towerOwnershipFilter"
          tabindex="0"
          class="tw:dropdown-content tw:menu tw:w-52 tw:rounded-box tw:bg-base-100 tw:p-2 tw:shadow">
          <li>
            <a @click="setTowerOwnershipTypeFilter(TowerOwnershipType.CreatedByMe)" class="tw:w-full tw:text-gray-700">
              Created By Me
              <Check
                :size="16"
                class="tw:text-green-500"
                v-if="selectedTowerOwnershipType === TowerOwnershipType.CreatedByMe" />
            </a>
          </li>

          <li>
            <a @click="setTowerOwnershipTypeFilter(TowerOwnershipType.WorkedOnByMe)" class="tw:w-full tw:text-gray-700">
              Worked On By Me
              <Check
                :size="16"
                class="tw:text-green-500"
                v-if="selectedTowerOwnershipType === TowerOwnershipType.WorkedOnByMe" />
            </a>
          </li>

          <li>
            <a @click="setTowerOwnershipTypeFilter(TowerOwnershipType.AllTowers)" class="tw:w-full tw:text-gray-700">
              All Towers
              <Check class="tw:text-green-500" v-if="selectedTowerOwnershipType === TowerOwnershipType.AllTowers" />
            </a>
          </li>
        </ul>
      </div>

      <!-- Tower Type Filter-->
      <div class="tw:dropdown tw:dropdown-bottom">
        <label
          tabindex="0"
          class="tw:btn tw:m-1 tw:rounded-md tw:bg-gray-200/50 tw:text-xs tw:shadow-xs tw:hover:bg-gray-200/100">
          {{ towerTypeLabel }}
          <ChevronDown :size="16" class="tw:font-bold tw:text-gray-600" />
        </label>
        <ul
          ref="towerTypeFilter"
          tabindex="0"
          class="tw:dropdown-content tw:menu tw:w-52 tw:rounded-box tw:bg-base-100 tw:p-2 tw:shadow">
          <li>
            <a @click="setTowerTypeFilter(undefined)" class="tw:w-full tw:text-gray-700">
              All Types
              <Check :size="16" class="tw:text-green-500" v-if="!selectedTowerType" />
            </a>
          </li>
          <li v-for="type in towerTypes" :key="type.name" class="">
            <a @click="setTowerTypeFilter(type)" class="tw:w-full tw:text-gray-700">
              {{ type.name }}
              <Check class="tw:text-green-500" v-if="selectedTowerType?.id === type.id" />
            </a>
          </li>
        </ul>
      </div>

      <button
        v-if="isFiltered"
        @click="resetFilters"
        :disabled="!isFiltered"
        class="tw:h-fit tw:p-0 tw:text-xs tw:font-bold tw:uppercase tw:transition-all tw:duration-100"
        :class="isFiltered ? 'tw:bg-white tw:text-red-500 tw:hover:scale-110' : 'tw:bg-red-50 tw:text-gray-400'">
        Reset
      </button>
    </div>

    <!--  card container -->
    <div v-if="loading" class="tw:mt-10 tw:flex tw:items-center tw:justify-center">
      <span class="tw:loading tw:loading-lg tw:loading-dots tw:text-gray-600"></span>
    </div>
    <div
      v-else
      class="tw:mt-8 tw:flex tw:w-10/12 tw:flex-row tw:flex-wrap tw:items-center tw:justify-center tw:gap-x-8 tw:gap-y-6 tw:self-center tw:sm:gap-x-4 tw:sm:gap-y-6 tw:lg:gap-x-4 tw:lg:gap-y-6 tw:xl:gap-x-10 tw:xl:gap-y-8 tw:2xl:gap-x-14 tw:2xl:gap-y-10">
      <!--    Tower creation cards-->
      <div
        class="tw:flex tw:h-56 tw:w-80 tw:flex-row tw:flex-wrap tw:items-center tw:justify-center tw:gap-x-6 tw:self-center">
        <!-- bound coverage card -->
        <div
          @click="openNewTowerModal(TowerType.BoundCoverage)"
          class="tw:group tw:tooltip tw:pointer-events-auto tw:tooltip-bottom tw:z-30 tw:flex tw:cursor-pointer tw:justify-items-center"
          :data-tip="BOUND_COVERAGE_TOOLTIP">
          <div
            class="tw:m-auto tw:flex tw:h-24 tw:w-36 tw:flex-col tw:justify-center tw:rounded-lg tw:border tw:border-solid tw:border-blue-200 tw:bg-white tw:text-[#133044] tw:shadow-md tw:transition-all tw:duration-150 tw:group-hover:border-blue-200 tw:group-hover:bg-blue-50 tw:group-hover:shadow-none">
            <div class="tw:flex tw:flex-col tw:items-center tw:gap-2 tw:self-center tw:p-2">
              <Plus class="tw:text-gray-400" />
              <h2 class="tw:text-center tw:text-sm tw:font-medium">Bound Coverage</h2>
            </div>
          </div>
        </div>

        <!-- new coverage card -->
        <div
          @click="openNewTowerModal(TowerType.NewCoverage)"
          class="tw:group tw:tooltip tw:pointer-events-auto tw:tooltip-bottom tw:z-30 tw:flex tw:cursor-pointer tw:justify-items-center"
          :data-tip="NEW_COVERAGE_TOOLTIP">
          <div
            class="tw:m-auto tw:flex tw:h-24 tw:w-36 tw:flex-col tw:justify-center tw:rounded-lg tw:border tw:border-solid tw:border-orange-200 tw:bg-white tw:text-[#133044] tw:shadow-md tw:transition-all tw:duration-150 tw:group-hover:border-blue-200 tw:group-hover:bg-blue-50 tw:group-hover:shadow-none">
            <div class="tw:flex tw:flex-col tw:items-center tw:gap-2 tw:self-center tw:p-2">
              <Plus class="tw:text-gray-400" />
              <h2 class="tw:text-center tw:text-sm tw:font-medium">New Coverage</h2>
            </div>
          </div>
        </div>

        <!-- new renewal card -->
        <div
          @click="openNewTowerModal(TowerType.Renewal)"
          class="tw:group tw:tooltip tw:pointer-events-auto tw:tooltip-bottom tw:z-20 tw:flex tw:cursor-pointer tw:justify-items-center"
          :data-tip="RENEWAL_TOOLTIP">
          <div
            class="tw:m-auto tw:flex tw:h-24 tw:w-36 tw:flex-col tw:justify-center tw:rounded-lg tw:border tw:border-solid tw:border-red-200 tw:bg-white tw:text-[#133044] tw:shadow-md tw:transition-all tw:duration-150 tw:group-hover:border-blue-200 tw:group-hover:bg-blue-50 tw:group-hover:shadow-none">
            <div class="tw:flex tw:flex-col tw:items-center tw:gap-2 tw:self-center tw:p-2">
              <Plus class="tw:text-gray-400" />
              <span class="tw:text-center tw:text-sm tw:font-medium">Renewal</span>
            </div>
          </div>
        </div>

        <!-- new scratch card -->
        <div
          @click="openNewTowerModal(TowerType.Scratch)"
          class="tw:group tw:tooltip tw:pointer-events-auto tw:tooltip-bottom tw:z-20 tw:flex tw:cursor-pointer tw:justify-items-center"
          :data-tip="SCRATCH_TOOLTIP">
          <div
            class="tw:m-auto tw:flex tw:h-24 tw:w-36 tw:flex-col tw:justify-center tw:rounded-lg tw:border tw:border-solid tw:border-emerald-200 tw:bg-white tw:text-[#133044] tw:shadow-md tw:transition-all tw:duration-150 tw:group-hover:border-blue-200 tw:group-hover:bg-blue-50 tw:group-hover:shadow-none">
            <div class="tw:flex tw:flex-col tw:items-center tw:gap-2 tw:self-center tw:p-2">
              <Plus class="tw:text-gray-400" />
              <span class="tw:text-center tw:text-sm tw:font-medium">Scratch</span>
            </div>
          </div>
        </div>
      </div>

      <TowerCard
        v-for="(card, index) in towerCards"
        :key="index"
        :card-data="card"
        @click="loadTower"
        @edit="openEditTowerModal"
        @duplicate="handleDuplicateTower"
        @delete="handleConfirmDeleteTower"
        @toggle-publish="handleTogglePublishTower"
        @convert:new-coverage="handleOpenConvertScratchTowerModal"
        @convert:current-coverage="handleOpenConvertNewCoverageAndRenewalTowerModal"
        :highlightTowerId="highlightTowerId"
        :highlightTowerBorderColor="highlightBorderColor"
        v-model:options-open="disableTowers" />
    </div>

    <CreateTowerModal
      :show="showCreateTowerModal"
      :tower-type="newTowerType"
      @close="showCreateTowerModal = false"
      @create="handleCreateTower" />

    <EditTowerModal
      v-if="showEditTowerModal"
      :tower-id="editTowerId"
      @close="showEditTowerModal = false"
      @update="handleEditTower" />

    <DeleteTowerModal
      :show="deleteTowerState.show"
      :tower-card="deleteTowerState.towerCard"
      :handle-cancel="() => (deleteTowerState = { show: false, towerCard: undefined })"
      :handle-action="(towerId: string | undefined) => handleDeleteTower(towerId)" />

    <ConvertScratchTowerModal
      :show="convertScratchTowerState.show"
      :towerId="convertScratchTowerState.towerId"
      @cancel="convertScratchTowerState = { show: false, towerId: undefined }"
      @convert="handleConvertScratchTower" />

    <ConvertNewCoverageAndRenewalTowerModal
      :show="convertNewCoverageAndRenewalTowerState.show"
      :towerId="convertNewCoverageAndRenewalTowerState.towerId"
      :accountId="convertNewCoverageAndRenewalTowerState.accountId"
      @cancel="convertNewCoverageAndRenewalTowerState = { show: false, towerId: undefined }"
      @convert="handleConvertNewCoverageAndRenewalTower" />
  </div>
</template>

<style></style>
